import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Card,
  Table,
  Switch,
  Input,
  Tag,
  Space,
  Typography,
  Segmented,
  message,
} from 'antd';
import {
  ProjectOutlined,
  SearchOutlined,
  EnvironmentOutlined,
} from '@ant-design/icons';
import { useTheme } from '../../../context/ThemeContext';
import { API_BASE_URL } from '../../../utils/config';

const { Title } = Typography;

const ProjelerPage = () => {
  const { darkMode } = useTheme();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('Tümü');
  const [updatingId, setUpdatingId] = useState(null);

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const filters = encodeURIComponent(JSON.stringify([{ field: 'fetch', value: 'projeler' }]));
      const res = await axios.get(`${API_BASE_URL}/atm/query?filters=${filters}`);
      const active = (res.data?.active_data || []).map(p => ({ ...p, aktiflik: 1 }));
      const passive = (res.data?.passive_data || []).map(p => ({ ...p, aktiflik: 0 }));
      setProjects([...active, ...passive]);
    } catch (e) {
      message.error(`Projeler yüklenemedi: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleToggleStatus = useCallback(async (record, checked) => {
    try {
      setUpdatingId(record.id);
      await axios.put(`${API_BASE_URL}/atm/proje/${record.id}/aktiflik`, {
        aktiflik: checked ? 1 : 0,
      });
      setProjects(prev =>
        prev.map(p => (p.id === record.id ? { ...p, aktiflik: checked ? 1 : 0 } : p))
      );
      message.success(`"${record.ad}" ${checked ? 'aktif' : 'pasif'} duruma alındı`);
    } catch (e) {
      message.error(`Durum güncellenemedi: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setUpdatingId(null);
    }
  }, []);

  const filteredProjects = useMemo(() => {
    let data = projects;
    if (statusFilter === 'Aktif') data = data.filter(p => p.aktiflik === 1);
    if (statusFilter === 'Pasif') data = data.filter(p => p.aktiflik !== 1);
    if (searchText.trim()) {
      const q = searchText.trim().toLocaleLowerCase('tr');
      data = data.filter(p => (p.ad || '').toLocaleLowerCase('tr').includes(q));
    }
    return data;
  }, [projects, statusFilter, searchText]);

  const columns = [
    {
      title: 'Proje Adı',
      dataIndex: 'ad',
      key: 'ad',
      sorter: (a, b) => (a.ad || '').localeCompare(b.ad || '', 'tr'),
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: 'Durum',
      dataIndex: 'aktiflik',
      key: 'aktiflik',
      width: 120,
      sorter: (a, b) => (a.aktiflik || 0) - (b.aktiflik || 0),
      render: (value) =>
        value === 1 ? <Tag color="green">Aktif</Tag> : <Tag color="red">Pasif</Tag>,
    },
    {
      title: 'Lokasyon Sayısı',
      dataIndex: 'lokasyon_sayisi',
      key: 'lokasyon_sayisi',
      width: 160,
      sorter: (a, b) => (a.lokasyon_sayisi || 0) - (b.lokasyon_sayisi || 0),
      render: (value) => (
        <Space>
          <EnvironmentOutlined />
          {value ?? 0}
        </Space>
      ),
    },
    {
      title: 'Aktif / Pasif',
      key: 'toggle',
      width: 130,
      render: (_, record) => (
        <Switch
          checked={record.aktiflik === 1}
          loading={updatingId === record.id}
          checkedChildren="Aktif"
          unCheckedChildren="Pasif"
          onChange={(checked) => handleToggleStatus(record, checked)}
        />
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <Card
        className={darkMode ? 'dark-card' : ''}
        title={
          <Space>
            <ProjectOutlined />
            <Title level={4} style={{ margin: 0 }}>Projeler</Title>
          </Space>
        }
        extra={
          <Space>
            <Segmented
              options={['Tümü', 'Aktif', 'Pasif']}
              value={statusFilter}
              onChange={setStatusFilter}
            />
            <Input
              allowClear
              placeholder="Proje ara..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 220 }}
            />
          </Space>
        }
      >
        <Table
          rowKey="id"
          size="middle"
          loading={loading}
          columns={columns}
          dataSource={filteredProjects}
          scroll={{ x: 'max-content' }}
          pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (t) => `Toplam ${t} proje` }}
        />
      </Card>
    </div>
  );
};

export default ProjelerPage;
